/**
 * Client wrapper for the modifier worker.
 *
 * Provides the authoritative async interface for modifier evaluation.
 */

import {
  callWorker,
  createSingletonWorker,
} from "../../workers/createWorkerRPC.ts";
import type {
  ModifierWorkerRequest,
  ModifierWorkerResult,
} from "./modifierTypes.ts";

const getWorker = createSingletonWorker(
  () =>
    new Worker(new URL("./modifier.worker.ts", import.meta.url), {
      type: "module",
    }),
);

/**
 * Evaluate a modifier stack asynchronously in the authoritative worker.
 *
 * Requests are intentionally NOT transferred: the caller typically passes
 * store-owned geometry and dependency snapshots that must remain usable on the
 * main thread. The worker transfers the result back because it owns the output.
 */
export async function evaluateModifierStackAsync(
  request: ModifierWorkerRequest,
): Promise<ModifierWorkerResult["geometry"]> {
  const result = await callWorker<ModifierWorkerRequest, ModifierWorkerResult>(
    getWorker(),
    request,
    [],
    {
      stallWarningMs: 10_000,
      stallMessage: "Modifier evaluation is taking longer than expected.",
      stallDedupKey: "modifier-stall",
    },
  );
  return result.geometry;
}
